// References https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/combobox_role
// and https://developer.mozilla.org/en-US/docs/Web/HTML/Element/select#accessibility_concerns
// and https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/

// Timekeeper has a specific select under the cog which can't be handled by this class
// Likewise, drop down lists in modals using the answer type "menu" in surveys clone all inputs without the events
const selects = document.querySelectorAll('select:not(.form-select-sm):not(.inTable)');

selects.forEach((select) => {
    // Currently don't know what to do with drop down lists in tables.
    if (select.parentElement.tagName === 'TD') return;

    const div = document.createElement('div');
    const header = document.createElement('div');
    const optgroups = select.querySelectorAll('optgroup');
    const span = document.createElement('span');
    const parent = select.parentElement;
    const multiple = select.hasAttribute('multiple');
    const optionPills = document.createElement('div');

    // Autocomplete fields
    let searchString = '';
    let searchTimeout;
    let storedOptions;

    // This recreates the base element where we're going to clone in all of the options
    // from the select. It consists of a div, header, span, button, datalist, and options
    if (!multiple) {
        span.innerText = select.label;
        span.classList.add('text-truncate');
        span.tabIndex = -1;
        header.appendChild(span);
    } else {
        div.setAttribute('multiple', '');
        div.setAttribute('aria-multiselectable', 'true');
        const emptySpan = document.createElement('span');
        emptySpan.classList.add('empty-span');
        optionPills.appendChild(emptySpan);
        optionPills.classList.add('option-pills');
        header.appendChild(optionPills);
    }

    // Creates the chevron button (for looks)
    const button = document.createElement('button');
    button.classList.add('glyphicons', 'glyphicons-chevron-down', 'h-100');
    button.tabIndex = -1;
    button.setAttribute('type', 'button');
    header.appendChild(button);

    header.classList.add('header-select', 'align-items-center');
    parent.insertBefore(div, select);
    header.appendChild(select);

    // Add properties to the parent div, make it tabable and remove the select from tabbing.
    const selectId = select.id;
    div.setAttribute('id', `select-clone-${selectId}`);
    div.setAttribute('aria-controls', `${selectId}-listbox`);
    div.setAttribute('role', 'combobox');
    div.setAttribute('aria-expanded', 'false');
    div.setAttribute('aria-haspopup', 'listbox');
    div.classList.add('select');
    div.tabIndex = 0;
    select.tabIndex = -1;

    // Change label to the side of field
    const selectLabel = document.querySelector(`[for=${selectId}]`);

    // Recruitment mail option to bulk update applicant status does
    // not have a label, and does not have space to place one.
    if (selectLabel) {
        selectLabel.setAttribute('for', `select-clone-${selectId}`);
        selectLabel.setAttribute('id', `${selectId}-label`);
    }

    createDatalist();

    if (select.hasAttribute('disabled')) {
        div.setAttribute('disabled', '');
        return;
    }

    function updateSingleSelectedOption(element, dispatchEvent = true) {
        // Remove old selected details
        const oldElement = div.querySelector('.option-selected');
        if (oldElement) {
            oldElement.setAttribute('aria-selected', 'false');
            oldElement.querySelector('.glyphicons').remove();
            oldElement.classList.remove('option-selected');
        }

        // Add details to newly selected option
        element.setAttribute('aria-selected', 'true');
        element.classList.add('option-selected');
        updateCurrentOption(element);

        // Add the tick to show the selected item
        const tick = document.createElement('div');
        tick.classList.add('glyphicons', 'glyphicons-ok', 'mr-auto');
        element.appendChild(tick);

        // Check for mandatory field
        span.innerText = element.dataset.label;
        singleMandatoryFieldCheck(span);

        // Change initial select value, and kick off Aurion change method
        select.value = element.dataset.value;
        if (dispatchEvent) select.dispatchEvent(new Event('change'));
    }

    function removeOptionPill(element) {
        element.setAttribute('aria-selected', 'false');
        element.querySelector('.glyphicons').remove();
        element.classList.remove('option-selected');
        // Remove the span
        optionPills.querySelector(`#pill-${element.id}`).remove();
        select.querySelector(`[value="${element.getAttribute('data-value')}"]`).selected = false;
        select.dispatchEvent(new Event('change'));

        multipleMandatoryFieldCheck();
    }

    function updateMultipleSelectedOption(element) {
        // Remove element details if already selected
        if (element.classList.contains('option-selected')) {
            removeOptionPill(element);
            return;
        }

        // Add details to newly selected option and add pill to header div
        // Create close button for the pill.
        const closeButton = document.createElement('span');
        closeButton.classList.add('glyphicons', 'glyphicons-remove');
        closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            removeOptionPill(element);
            e.stopPropagation();
        });

        // Create the pill and append the close button.
        const optSpan = document.createElement('span');
        optSpan.classList.add('opt-span');
        optSpan.setAttribute('id', `pill-${element.id}`);
        optSpan.setAttribute('data-value', element.dataset.value);
        optSpan.innerText = element.dataset.label;
        optSpan.appendChild(closeButton);
        optionPills.insertBefore(optSpan, header.querySelector('.empty-span'));

        // Make the option selected.
        element.setAttribute('aria-selected', 'true');
        element.classList.add('option-selected');
        updateCurrentOption(element);

        // Add the tick to show the selected item
        const tick = document.createElement('div');
        tick.classList.add('glyphicons', 'glyphicons-ok', 'mr-auto');
        element.appendChild(tick);

        // Check for mandatory field
        multipleMandatoryFieldCheck();

        // Change initial select value, and kick off Aurion change method
        select.querySelector(`[value="${element.getAttribute('data-value')}"]`).selected = true;
        select.dispatchEvent(new Event('change'));
    }

    function updateCurrentOption(element) {
        div.querySelector('.option-current')?.classList.remove('option-current');
        element.classList.add('option-current');
        div.setAttribute('aria-activedescendant', `${element.id}`);
        maintainScrollVisibility(element);
    }

    function multipleMandatoryFieldCheck() {
        if (!div.querySelector('.opt-span') && select.hasAttribute('required')) header.classList.add('form-control-danger');
        else header.classList.remove('form-control-danger');
    }

    function singleMandatoryFieldCheck(element) {
        if (select.hasAttribute('required') && !element.innerHTML) header.classList.add('form-control-danger');
        else header.classList.remove('form-control-danger');
    }

    // On option click
    function onclick(_e) {
        // Change the selected option
        if (multiple) updateMultipleSelectedOption(this);
        else updateSingleSelectedOption(this);
    }

    function onkeyup(e) {
        e.preventDefault();
        e.stopPropagation();

        if (e.code === 'Enter') this.click();
    }

    // Clone the options into the datalist
    function createDatalist() {
        // Remove and rebuild datalist (Some places like Leave rebuild the options on the fly)
        if (div.querySelector('datalist')) div.querySelector('datalist').remove();

        const datalist = document.createElement('datalist');
        div.appendChild(datalist);

        const options = Array.from(select.options);

        storedOptions = [];

        // Begin creating options
        let i = 0;
        options.forEach((o) => {
            const option = document.createElement('div');
            const label = document.createElement('div');
            o.attributes.forEach((attribute) => {
                // Only copy dataset values.
                if (['value', 'selected'].includes(attribute.name)) {
                    option.dataset[attribute.name] = attribute.value;
                }
            });
            option.classList.add('option', 'd-flex');
            option.setAttribute('role', 'option');
            option.setAttribute('id', `${selectId}-option-${i}`);
            i += 1;
            label.classList.add('label', 'w-100');
            label.innerText = o.label;

            // Set up cloned options
            option.dataset.value = o.value;
            option.dataset.label = o.label;
            if (!o.label) option.setAttribute('aria-label', 'leave empty');

            // Set aria-selected
            option.setAttribute('aria-selected', 'false');
            const { selectedOptions } = select;
            if (Array.from(selectedOptions).includes(o)) {
                if (multiple) updateMultipleSelectedOption(option);
                else updateSingleSelectedOption(option);
                updateCurrentOption(option);
            }

            option.onclick = onclick;
            option.onkeyup = onkeyup;
            option.prepend(label);
            storedOptions.push(option);
            datalist.appendChild(option);
        });

        div.appendChild(header);
        optgroups.forEach((o) => {
            const optgroup = document.createElement('div');
            const label = document.createElement('div');
            const allOptions = o.querySelectorAll('option');

            Object.assign(optgroup, o);
            optgroup.classList.add('optgroup');
            label.classList.add('label');
            label.innerText = o.label;
            optgroup.appendChild(label);
            div.appendChild(optgroup);
            allOptions.forEach((opt) => {
                const option = document.createElement('div');
                const optionLabel = document.createElement('div');

                opt.attributes.forEach((attribute) => {
                    option.dataset[attribute.name] = attribute.value;
                });
                option.classList.add('option');
                optionLabel.classList.add('label');
                optionLabel.innerText = opt.label;
                option.dataset.value = opt.value;
                option.dataset.label = opt.label;
                option.onclick = onclick;
                option.onkeyup = onkeyup;
                option.appendChild(optionLabel);
                optgroup.appendChild(option);
            });
        });

        // Prep datalist for appending
        datalist.setAttribute('role', 'listbox');
        datalist.setAttribute('id', `${select.id}-listbox`);
        datalist.tabIndex = -1;

        if (!multiple) {
            // Clear the combobox in event of options changing.
            span.innerText = select.selectedOptions[0]?.text || '';
            // Check if the field is mandatory and empty
            singleMandatoryFieldCheck(span);
        }
    }

    // Open up the data list using css.
    function manageDataListVisibility(event) {
        event.preventDefault();
        const open = div.hasAttribute('data-open');
        event.stopPropagation();
        if (open) {
            div.removeAttribute('data-open');
            div.setAttribute('aria-expanded', 'false');
        } else {
            openDatalist();
        }
    }

    function openDatalist() {
        if (!div.hasAttribute('data-open')) {
            // Remove any already open datalists not caught with other document.eventlistener.
            document.querySelector('[data-open]')?.removeAttribute('data-open');

            // Check size of input field and open datalist below that.
            div.querySelector('datalist').style.top = `${div.offsetHeight}px`;
            div.setAttribute('data-open', '');
            div.setAttribute('aria-expanded', 'true');

            const currenOpt = div.querySelector('.option-current') || storedOptions[0];
            maintainScrollVisibility(currenOpt);
        }
    }

    div.onclick = (e) => {
        e.preventDefault();
        manageDataListVisibility(e);
    };

    div.onkeyup = (event) => {
        if (event.code === 'Enter') {
            event.preventDefault();
            if (div.hasAttribute('data-open')) div.querySelector('.option-current')?.click();
            else div.click();
        }
    };

    // Add ability to use up/down arrows to select different options
    div.onkeydown = (event) => {
        // handle typing characters when open or closed
        if (
            event.code === 'Backspace'
            || event.code === 'Clear'
            || (event.key.length === 1 && event.key !== ' ' && !event.altKey && !event.ctrlKey && !event.metaKey)
        ) {
            autocomplete(event.key);
        }

        if (!div.hasAttribute('data-open')) return;

        if (event.code === 'ArrowDown' || event.code === 'ArrowUp') {
            event.preventDefault();

            // Error handling: If drop down has no or only one option
            const currentOption = div.querySelector('.option-current');
            if (!storedOptions.length) return;

            if (!currentOption) {
                if (event.code === 'ArrowDown') updateCurrentOption(storedOptions[0]);
                else updateCurrentOption(storedOptions[storedOptions.length - 1]);
                return;
            }

            // Cycle through the different options.
            if (event.code === 'ArrowDown') {
                const nextOption = currentOption.nextElementSibling;
                if (nextOption) updateCurrentOption(nextOption);
                else updateCurrentOption(currentOption.parentElement.firstChild);
            } else {
                const prevOption = currentOption.previousElementSibling;
                if (prevOption) updateCurrentOption(prevOption);
                else updateCurrentOption(currentOption.parentElement.lastChild);
            }
            return;
        }

        if (event.code === 'Space' || event.code === 'Tab') {
            div.querySelector('.option-current')?.click();
            return;
        }

        if (event.code === 'Escape') {
            manageDataListVisibility(event);
            return;
        }

        // For the rest of these options we want to preventDefault
        event.preventDefault();

        if (event.code === 'Home') {
            updateCurrentOption(storedOptions[0]);
            return;
        }

        if (event.code === 'End') {
            updateCurrentOption(storedOptions[storedOptions.length - 1]);
            return;
        }

        if (event.code === 'PageDown') {
            const currentOption = storedOptions.find((opt) => opt.classList.contains('option-current'));
            const currentIndex = storedOptions.indexOf(currentOption);
            const newOption = storedOptions[currentIndex + 10]
                || storedOptions[storedOptions.length - 1];
            updateCurrentOption(newOption);
            return;
        }

        if (event.code === 'PageUp') {
            const currentOption = storedOptions.find((opt) => opt.classList.contains('option-current'));
            const currentIndex = storedOptions.indexOf(currentOption);
            const newOption = storedOptions[currentIndex - 10]
                || storedOptions[0];
            updateCurrentOption(newOption);
        }
    };

    document.addEventListener('click', (_e) => {
        if (div.hasAttribute('data-open')) {
            div.removeAttribute('data-open');
            div.setAttribute('aria-expanded', 'false');
        }
    });

    header.addEventListener('click', (e) => {
        e.preventDefault();
        header.parentElement.focus();
    });

    function autocomplete(letter) {
        // open the listbox if it is closed
        openDatalist();

        // find the index of the first matching option
        updateSearchString(letter);

        const activeOption = storedOptions.find((opt) => opt.classList.contains('option-current'));
        const activeIndex = storedOptions.indexOf(activeOption) || 0;
        const searchIndex = getIndexByLetter(
            searchString,
            activeIndex,
        );

        // if a match was found, go to it
        if (searchIndex >= 0) {
            // make option found in search index current options
            updateCurrentOption(storedOptions[searchIndex]);
            return;
        }

        // if no matches, clear the timeout and search string
        window.clearTimeout(searchTimeout);
        searchString = '';
    }

    /**
     * Checks to see if user has entered anything recently, if not wipes selection.
     * Otherwise, return the current search string
     *
     * @param {String} char
     * @returns
     */
    function updateSearchString(char) {
        // reset typing timeout and start new timeout
        // this allows us to make multiple-letter matches, like a native select
        if (typeof searchTimeout === 'number') {
            window.clearTimeout(searchTimeout);
        }

        searchTimeout = window.setTimeout(() => { searchString = ''; }, 500);

        // add most recent letter to saved search string
        searchString += char;
        return searchString;
    }

    // return the index of an option from an array of options, based on a search string
    // if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
    function getIndexByLetter(filter, startIndex = 0) {
        const optionsToFilter = storedOptions.map((opt) => opt.innerText);
        const orderedOptions = [
            ...optionsToFilter.slice(startIndex),
            ...optionsToFilter.slice(0, startIndex),
        ];
        const firstMatch = filterOptions(orderedOptions, filter)[0];
        const allSameLetter = (array) => array.every((letter) => letter === array[0]);

        // first check if there is an exact match for the typed string
        if (firstMatch) {
            return optionsToFilter.indexOf(firstMatch);
        }

        // if the same letter is being repeated, cycle through first-letter matches
        if (allSameLetter(filter.split(''))) {
            const matches = filterOptions(orderedOptions || [], filter[0]);
            return optionsToFilter.indexOf(matches[1]);
        }

        // if no matches, return -1
        return -1;
    }

    // filter an array of options against an input string
    // returns an array of options that begin with the filter string, case-independent
    function filterOptions(searchOptions, filter) {
        return searchOptions.filter((option) => option
            .toLowerCase().indexOf(filter.toLowerCase()) === 0);
    }

    // ensure a given child element is within the parent's visible scroll area
    // if the child is not visible, scroll the parent
    function maintainScrollVisibility(activeElement) {
        const scrollParent = div.querySelector('datalist');
        const { offsetHeight, offsetTop } = activeElement;
        const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

        const isAbove = offsetTop < scrollTop;
        const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

        if (isAbove) {
            scrollParent.scrollTo(0, offsetTop);
        } else if (isBelow) {
            scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
        }

        // Check for floating button bar.
        const footer = document.querySelector('.floating-button-bar-sticky.is-floating');
        const footerHeight = footer ? footer.offsetHeight : 0;
        // 60 is the hardcoded height for the header around the application
        const modalHeader = div.querySelector('.modal-header');
        const headerHeight = modalHeader ? modalHeader.offsetHeight : 60;

        // ensure the new option is visible on screen
        // ensure the new option is in view
        if (!isElementInView(activeElement, footerHeight, headerHeight)) {
            if (footer) activeElement.style.setProperty('scroll-margin-bottom', `${footerHeight}px`);
            if (headerHeight !== 60) activeElement.style.setProperty('scroll-margin-top', `${headerHeight}`);
            activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }

    // check if element is visible in browser view port
    function isElementInView(element, footerHeight, headerHeight) {
        const bounding = element.getBoundingClientRect();

        return (
            bounding.left >= 0
            && bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
            && bounding.top >= headerHeight
            && bounding.bottom <= (window.innerHeight - footerHeight
                || document.documentElement.clientHeight - footerHeight)
        );
    }

    // This is used in ERM as values are programatically sent to the select element after it's already loaded.
    // This is not picked up the mutation observer and must be activated manually.
    select.addEventListener('refreshSelectedOption', (_e) => {
        if (!storedOptions.length) return;
        updateSingleSelectedOption(storedOptions[select.selectedIndex]);
    });

    select.addEventListener('resetSelect', (_e) => {
        if (!multiple) updateSingleSelectedOption(storedOptions[0]);
        else {
            if (!div.querySelectorAll('.option-selected').length) return;
            div.querySelectorAll('.option-selected').forEach((pill) => {
                removeOptionPill(pill);
            });
        }
    });

    // Check if select data is changed.
    const observer = new MutationObserver(() => createDatalist());
    observer.observe(select, { childList: true });

    // Set beginning text, and mandatory field-ness
    if (multiple) multipleMandatoryFieldCheck();
    else {
        // span.innerText = select.selectedOptions[0]?.text || '';
        singleMandatoryFieldCheck(span);
    }
});

document.querySelectorAll('form').forEach((form) => {
    form.addEventListener('reset', (_e) => {
        form.querySelectorAll('select').forEach((sel) => {
            sel.dispatchEvent(new Event('resetSelect'));
        });
    });
});
